<script>
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

export default {
  components: {
    NextButton,
    Spinner,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      phoneNumbers: [],
      isLoading: false,
      loadError: '',
    };
  },
  mounted() {
    this.fetchPhoneNumbers();
  },
  methods: {
    async fetchPhoneNumbers() {
      this.isLoading = true;
      this.loadError = '';
      try {
        const { data } = await WhatsappTemplatesAPI.getPhoneNumbers(
          this.inbox.id
        );
        this.phoneNumbers = data.phone_numbers || [];
      } catch (error) {
        this.loadError =
          error?.response?.data?.error ||
          this.$t('INBOX_MGMT.WHATSAPP_NUMBERS.ERROR');
      } finally {
        this.isLoading = false;
      }
    },
    qualityClass(rating) {
      if (rating === 'GREEN') return 'bg-n-teal-3 text-n-teal-11';
      if (rating === 'YELLOW') return 'bg-n-amber-3 text-n-amber-11';
      if (rating === 'RED') return 'bg-n-ruby-3 text-n-ruby-11';
      return 'bg-n-slate-3 text-n-slate-11';
    },
    verificationClass(status) {
      return status === 'VERIFIED'
        ? 'bg-n-teal-3 text-n-teal-11'
        : 'bg-n-slate-3 text-n-slate-11';
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.TITLE') }}
        </h3>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        :is-loading="isLoading"
        :label="$t('INBOX_MGMT.WHATSAPP_NUMBERS.REFRESH')"
        @click="fetchPhoneNumbers"
      />
    </div>

    <div v-if="isLoading" class="flex justify-center py-8">
      <Spinner class="size-5 text-n-slate-11" />
    </div>

    <p v-else-if="loadError" class="py-4 text-sm text-n-ruby-11">
      {{ loadError }}
    </p>

    <p v-else-if="!phoneNumbers.length" class="py-4 text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.EMPTY') }}
    </p>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-11">
            <th class="py-2 pr-4 font-medium">
              {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.COLUMNS.NUMBER') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.COLUMNS.DISPLAY_NAME') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.COLUMNS.VERIFICATION') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.COLUMNS.QUALITY') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.COLUMNS.LIMIT') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="number in phoneNumbers"
            :key="number.id"
            class="border-t border-n-weak"
          >
            <td class="py-3 pr-4">
              <span class="font-medium text-n-slate-12">
                {{ number.phone_number }}
              </span>
              <span
                v-if="number.connected_to_this_inbox"
                class="inline-block px-2 py-0.5 ml-2 rounded-md text-xs font-medium bg-n-teal-3 text-n-teal-11"
              >
                {{ $t('INBOX_MGMT.WHATSAPP_NUMBERS.CONNECTED') }}
              </span>
            </td>
            <td class="py-3 pr-4 text-n-slate-11">
              {{ number.display_name || '—' }}
            </td>
            <td class="py-3 pr-4">
              <span
                class="inline-block px-2 py-0.5 rounded-md text-xs font-medium"
                :class="verificationClass(number.verification_status)"
              >
                {{ number.verification_status || '—' }}
              </span>
            </td>
            <td class="py-3 pr-4">
              <span
                class="inline-block px-2 py-0.5 rounded-md text-xs font-medium"
                :class="qualityClass(number.quality_rating)"
              >
                {{ number.quality_rating || '—' }}
              </span>
            </td>
            <td class="py-3 pr-4 text-n-slate-11">
              {{ number.messaging_limit || '—' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
